import React, { useEffect, useState } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar.css';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Menu,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudSync as CalendarSyncIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { ticketsApi } from '../services/api';
import { Ticket } from '../types/api';
import calDAVService from '../services/calDAVService';
import { useNavigate } from 'react-router-dom';

// Setup the localizer for date-fns with German locale
const locales = {
  de: de,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom messages in German
const messages = {
  allDay: 'Ganztägig',
  previous: 'Zurück',
  next: 'Weiter',
  today: 'Heute',
  month: 'Monat',
  week: 'Woche',
  day: 'Tag',
  agenda: 'Agenda',
  date: 'Datum',
  time: 'Zeit',
  event: 'Ticket',
  noEventsInRange: 'Keine Tickets in diesem Zeitraum.',
  showMore: (total: number) => `+ ${total} weitere`,
};

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: Ticket;
}

const TicketCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [view, setView] = useState<View>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [statusFilter, setStatusFilter] = useState<string>('Alle');
  const [calDAVSettings, setCalDAVSettings] = useState<any[]>([]);
  const [syncMenuAnchor, setSyncMenuAnchor] = useState<null | HTMLElement>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadTickets();
    loadCalDAVSettings();
  }, []);

  useEffect(() => {
    // Convert tickets to calendar events whenever tickets or filter changes
    const filteredTickets = statusFilter === 'Alle'
      ? tickets
      : tickets.filter(t => t.status === statusFilter);

    const calendarEvents: CalendarEvent[] = filteredTickets
      .filter(ticket => ticket.wann) // Only tickets with a date
      .map(ticket => {
        const date = new Date(ticket.wann!);
        return {
          id: ticket.ticketID,
          title: ticket.titel,
          start: date,
          end: date,
          resource: ticket,
        };
      });

    setEvents(calendarEvents);
  }, [tickets, statusFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await ticketsApi.getAll();
      setTickets(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError('Fehler beim Laden der Tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadCalDAVSettings = async () => {
    try {
      const settings = await calDAVService.getAllSettings();
      setCalDAVSettings(settings.filter((s: any) => s.aktiv));
    } catch (err) {
      console.error('Error loading CalDAV settings:', err);
    }
  };

  const handleSyncWithCalDAV = async (settingId: number) => {
    try {
      setSyncing(true);
      setSyncMenuAnchor(null);
      const result = await calDAVService.syncAllTickets(settingId);
      setSuccess(`Synchronisation erfolgreich: ${result.ticketsSynced} Tickets synchronisiert`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler bei der Synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenSyncMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSyncMenuAnchor(event.currentTarget);
  };

  const handleCloseSyncMenu = () => {
    setSyncMenuAnchor(null);
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedTicket(event.resource);
    setDialogOpen(true);
  };

  const handleNavigate = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
  };

  const handleStatusChange = async (ticketId: number, newStatus: string) => {
    try {
      await ticketsApi.updateStatus(ticketId, { status: newStatus });
      // Reload tickets to update the calendar
      await loadTickets();
      setDialogOpen(false);
    } catch (err) {
      console.error('Error updating ticket status:', err);
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'hoch':
        return 'error';
      case 'mittel':
        return 'warning';
      case 'niedrig':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Erledigt':
        return 'success';
      case 'In Bearbeitung':
        return 'info';
      case 'Offen':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Custom event style based on status
  const eventStyleGetter = (event: CalendarEvent) => {
    const ticket = event.resource;
    let backgroundColor = '#3174ad';

    switch (ticket.status) {
      case 'Erledigt':
        backgroundColor = '#4caf50';
        break;
      case 'In Bearbeitung':
        backgroundColor = '#2196f3';
        break;
      case 'Offen':
        backgroundColor = '#ff9800';
        break;
    }

    // Check if overdue (only for non-completed tickets)
    if (ticket.status !== 'Erledigt' && ticket.wann) {
      const dueDate = new Date(ticket.wann);
      const now = new Date();
      if (dueDate < now) {
        backgroundColor = '#f44336'; // Red for overdue
      }
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Ticket Kalender
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="Alle">Alle</MenuItem>
              <MenuItem value="Offen">Offen</MenuItem>
              <MenuItem value="In Bearbeitung">In Bearbeitung</MenuItem>
              <MenuItem value="Erledigt">Erledigt</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Kalender aktualisieren">
            <IconButton onClick={loadTickets} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {calDAVSettings.length > 0 && (
            <>
              <Tooltip title="Mit CalDAV synchronisieren">
                <IconButton
                  onClick={handleOpenSyncMenu}
                  disabled={syncing}
                  color="primary"
                >
                  {syncing ? <CircularProgress size={24} /> : <CalendarSyncIcon />}
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={syncMenuAnchor}
                open={Boolean(syncMenuAnchor)}
                onClose={handleCloseSyncMenu}
              >
                {calDAVSettings.map((setting: any) => (
                  <MenuItem
                    key={setting.calDAVSettingID}
                    onClick={() => handleSyncWithCalDAV(setting.calDAVSettingID)}
                  >
                    <CalendarSyncIcon sx={{ mr: 1 }} fontSize="small" />
                    {setting.name}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Tooltip title="CalDAV-Einstellungen">
            <IconButton onClick={() => navigate('/einstellungen')}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, height: 'calc(100vh - 200px)', minHeight: '600px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          messages={messages}
          culture="de"
          view={view}
          onView={setView}
          date={currentDate}
          onNavigate={handleNavigate}
          eventPropGetter={eventStyleGetter}
        />
      </Paper>

      {/* Ticket Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>{selectedTicket.titel}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Beschreibung (Was)
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedTicket.was}
                </Typography>

                {selectedTicket.wie && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Vorgehensweise (Wie)
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {selectedTicket.wie}
                    </Typography>
                  </>
                )}

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedTicket.status}
                    color={getStatusColor(selectedTicket.status)}
                    size="small"
                  />
                  {selectedTicket.prioritaet && (
                    <Chip
                      label={selectedTicket.prioritaet}
                      color={getPriorityColor(selectedTicket.prioritaet)}
                      size="small"
                    />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  <strong>Frist:</strong>{' '}
                  {selectedTicket.wann
                    ? format(new Date(selectedTicket.wann), 'dd.MM.yyyy HH:mm', { locale: de })
                    : 'Keine Frist'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Erstellt am:</strong>{' '}
                  {format(new Date(selectedTicket.erstelltAm), 'dd.MM.yyyy HH:mm', { locale: de })}
                </Typography>

                {selectedTicket.erledigtAm && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Erledigt am:</strong>{' '}
                    {format(new Date(selectedTicket.erledigtAm), 'dd.MM.yyyy HH:mm', { locale: de })}
                  </Typography>
                )}

                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status ändern</InputLabel>
                    <Select
                      value={selectedTicket.status}
                      label="Status ändern"
                      onChange={(e) =>
                        handleStatusChange(selectedTicket.ticketID, e.target.value)
                      }
                    >
                      <MenuItem value="Offen">Offen</MenuItem>
                      <MenuItem value="In Bearbeitung">In Bearbeitung</MenuItem>
                      <MenuItem value="Erledigt">Erledigt</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TicketCalendar;
